import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import api from '../../lib/api';

const RING_R = 52;
const CIRC = 2 * Math.PI * RING_R;

export default function HealthScoreCard({ refreshKey, hasPlan }) {
  const navigate = useNavigate();
  const [score, setScore] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    let active = true;
    api.get('/health-score')
      .then(({ data }) => { if (active) { setScore(data); setNotFound(false); } })
      .catch((e) => { if (active && e.response?.status === 404) setNotFound(true); });
    return () => { active = false; };
  }, [refreshKey]);

  if (notFound || hasPlan === false) {
    return (
      <div data-testid="health-score-empty" className="bg-white border border-[#E5E5EB] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[#0A0A0B] mb-1">Your Financial Health Score</h3>
          <p className="text-sm text-[#5C5C66]">Create your first plan to unlock your 0-100 score.</p>
        </div>
        <button
          data-testid="create-plan-cta"
          onClick={() => navigate('/app')}
          className="bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl px-6 py-3 font-semibold whitespace-nowrap"
        >
          Create My Plan
        </button>
      </div>
    );
  }

  if (!score) {
    return <div className="bg-white border border-[#E5E5EB] rounded-2xl p-8 text-[#5C5C66] text-sm">Loading your score...</div>;
  }

  const pct = score.score / 100;
  const color = score.score >= 70 ? '#10B981' : score.score >= 40 ? '#534AB7' : '#F59E0B';

  const bars = [
    { label: 'Savings rate', points: score.savings_points, max: score.savings_max },
    { label: 'Debt ratio', points: score.debt_points, max: score.debt_max },
    { label: 'Goal progress', points: score.goal_points, max: score.goal_max },
  ];

  return (
    <div data-testid="health-score-card" className="bg-white border border-[#E5E5EB] rounded-2xl p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
        <div className="relative w-32 h-32 flex-shrink-0">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r={RING_R} fill="none" stroke="#F0F0F4" strokeWidth="10" />
            <circle
              cx="60" cy="60" r={RING_R} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - pct)}
              style={{ transition: 'stroke-dashoffset 0.8s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span data-testid="health-score-value" className="text-3xl font-extrabold text-[#0A0A0B]">{score.score}</span>
            <span className="text-xs text-[#A1A1AA] font-medium">/ 100</span>
          </div>
        </div>

        <div className="flex-1 w-full">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-[#0A0A0B]">Financial Health Score</h3>
            <button data-testid="score-info-btn" onClick={() => setShowInfo(!showInfo)} className="text-[#A1A1AA] hover:text-[#534AB7]">
              <Info size={16} />
            </button>
          </div>
          <p data-testid="score-message" className="text-sm text-[#5C5C66] mb-4">{score.message}</p>
          {showInfo && (
            <p data-testid="score-explanation" className="text-xs text-[#5C5C66] bg-[#F6F6F9] rounded-xl p-3 mb-4 leading-relaxed">
              Your score combines three things: how close your savings are to a 3-month emergency fund (40 pts),
              how low your debt is compared to your income (35 pts), and how many plan steps you've completed (25 pts).
            </p>
          )}
          <div className="space-y-2.5">
            {bars.map((b) => (
              <div key={b.label} className="flex items-center gap-3">
                <span className="text-xs font-medium text-[#5C5C66] w-24 flex-shrink-0">{b.label}</span>
                <div className="flex-1 h-2 bg-[#F0F0F4] rounded-full overflow-hidden">
                  <div className="h-full bg-[#534AB7] rounded-full" style={{ width: `${(b.points / b.max) * 100}%`, transition: 'width 0.6s ease' }} />
                </div>
                <span className="text-xs font-semibold text-[#0A0A0B] w-12 text-right">{b.points}/{b.max}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
